import type { BaseAPIResponse } from '~/types/api';
import type { Contact, MinorEligibility } from '~/types/person/contacts';
import { ApiError } from '~/utils/errors';

interface ContactsResponse {
    data: Contact[];
    self?: Contact;
}

export const contactsService = {
    async getContacts(personId: number, includeSelf: boolean = false): Promise<ContactsResponse> {
        const nuxtApp = useNuxtApp();
        
        // Base URL already has query params (?per_page=10000&blocked=false), so use & to append
        const url = includeSelf 
            ? `${nuxtApp.$api.routes.people.contacts.get_contacts(personId)}&include_self=true`
            : nuxtApp.$api.routes.people.contacts.get_contacts(personId);
        
        const { data, status, message } = await usePbApi<BaseAPIResponse<Contact[]> & { self?: Contact }>(
            url,
            {
                method: 'GET',
            }
        );
        
        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch contacts', status);
        }
        
        if (!data) {
            throw new ApiError('No contacts data received', 404);
        }
        
        return {
            data: (data as BaseAPIResponse<Contact[]>).data,
            self: (data as any).self,
        };
    },

    async createContact(personId: number, contactData: any): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.contacts.create_contact(personId),
                {
                    method: 'POST',
                    body: [contactData],
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to create contact', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async checkMinorEligibility(personId: number): Promise<MinorEligibility> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<MinorEligibility>(
            nuxtApp.$api.routes.people.contacts.minor_eligibility(personId),
            { method: 'GET' }
        );

        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to check minor eligibility', status);
        }

        return data;
    },

    async signMinorConsent(personId: number, signerLegalName: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.contacts.sign_minor_consent(personId),
                {
                    method: 'POST',
                    body: { signer_legal_name: signerLegalName },
                }
            );

            if (status !== 200 && status !== 204) {
                throw new ApiError(message || 'Failed to sign minor consent', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async createMinorChild(personId: number, childData: {
        first_name: string;
        last_name: string;
        dob: string;
    }): Promise<Contact> {
        const nuxtApp = useNuxtApp();

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<Contact>>(
                nuxtApp.$api.routes.people.contacts.create_minor(personId),
                {
                    method: 'POST',
                    body: childData,
                }
            );

            if (status !== 200 && status !== 201) {
                throw new ApiError(message || 'Failed to create minor child', status);
            }

            if (!data) {
                throw new ApiError('No contact data received', 404);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async deleteContact(personId: number, contactId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.contacts.delete(personId, contactId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 204) {
                throw new ApiError(message || 'Failed to delete contact', status);
            }
        } catch (error: any) {
            throw new Error(error.data?.message || error.message || 'Failed to delete contact');
        }
    }
}; 